import Phone from '@mui/icons-material/Phone'
import Print from '@mui/icons-material/Print'
import { Button, Paper, Stack, Typography } from '@mui/material'
import { AnnouncementList } from '../announcements/AnnouncementList'
import type { Position } from '../api/generated/Position'
import type { Shift } from '../api/generated/Shift'
import { useApi } from '../api/useApi'
import { VolunteerSpace } from '../assignments/VolunteerSpace'
import { useEvent } from '../events/EventContext'
import { SectionPage } from '../events/SectionPage'
import { HelpCard } from '../help/HelpCard'
import { InstallGuide } from '../install/InstallGuide'
import { PushSetup } from '../notifications/PushSetup'
import { planningDays } from '../planning/grid'
import { MyAnswers } from '../questions/MyAnswers'
import { MyPreferences } from '../volunteers/MyPreferences'

/** The volunteer's home in the event: setup of the app, announcements, organizers' phone. */
export function VolunteerHome() {
  const { event } = useEvent()
  return (
    <SectionPage title="Accueil" maxWidth="sm">
      <Stack spacing={3}>
        <HelpCard />
        <InstallGuide />
        <PushSetup />
        <AnnouncementList slug={event.slug} timezone={event.timezone} role={event.role} />
        {event.contact_phone && (
          <Button
            variant="outlined"
            startIcon={<Phone />}
            href={`tel:${event.contact_phone}`}
            sx={{ alignSelf: 'flex-start' }}
          >
            Appeler les organisateurs ({event.contact_phone})
          </Button>
        )}
      </Stack>
    </SectionPage>
  )
}

/** Landing page after registering or from the invitation: sets up the device first. */
export function WelcomePage() {
  const { event } = useEvent()
  return (
    <SectionPage title="Bienvenue" maxWidth="sm">
      <Stack spacing={3}>
        <Typography>
          Préparez votre téléphone pour recevoir vos rappels et les messages des organisateurs. Les
          organisateurs vous affectent ensuite à des créneaux selon vos disponibilités.
        </Typography>
        <InstallGuide />
        <PushSetup />
        <Button variant="contained" href={`/events/${event.slug}`} sx={{ alignSelf: 'flex-start' }}>
          Continuer
        </Button>
      </Stack>
    </SectionPage>
  )
}

export function MyPlanningPage() {
  const { event } = useEvent()
  return (
    <SectionPage
      title="Mon planning"
      maxWidth="sm"
      actions={
        <Button startIcon={<Print />} href={`/events/${event.slug}/print`}>
          Imprimer
        </Button>
      }
    >
      <Stack spacing={3}>
        <VolunteerSpace
          slug={event.slug}
          timezone={event.timezone}
          withdrawalDeadlineHours={event.withdrawal_deadline_hours}
        />
      </Stack>
    </SectionPage>
  )
}

export function MyAvailabilityPage() {
  const { event } = useEvent()
  const shifts = useApi<Shift[]>(`/events/${event.slug}/shifts`)
  const positions = useApi<Position[]>(`/events/${event.slug}/positions`)
  return (
    <SectionPage title="Mes disponibilités et postes" maxWidth="sm">
      <Stack spacing={3}>
        {shifts.data && positions.data && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <MyPreferences
              slug={event.slug}
              days={planningDays(shifts.data, event)}
              positions={positions.data}
            />
          </Paper>
        )}
        <MyAnswers slug={event.slug} />
      </Stack>
    </SectionPage>
  )
}
